/**
 * The upgrade dialog: the three tiers of PLAN §9, side by side, raised by
 * whatever the user just ran into.
 *
 * ## The shape of this interaction
 *
 * The recipe *appears* first. The editor is optimistic — that is the whole
 * point of the local-first store — so pressing "New recipe" at the cap creates
 * a row, opens it, and then the server's rejection arrives a few hundred
 * milliseconds later and the reconciliation removes it again. A toast alone
 * would leave the brewer wondering what happened to the recipe they were just
 * looking at, so the rejection gets a dialog that says what the cap is and what
 * lifts it.
 *
 * The alternative — pre-checking the cap on the button and never creating the
 * recipe — was rejected deliberately; `lib/plan.ts` explains why.
 *
 * ## Why the recommendation depends on the trigger
 *
 * Three columns is a comparison, not a funnel. Hitting the recipe cap is a
 * **Brewmaster** problem: that is the tier that fixes it, and recommending the
 * dearer one for it would be an upsell the trigger does not support. The
 * assistant, fermentation charts and Insights are **Beerwright** problems,
 * because Beerwright is the only tier that carries them — so pointing an
 * out-of-messages user at Brewmaster would sell them nothing they asked for.
 * The mapping lives in `lib/plan.ts` (`recommendedPlan`) so the drawer and the
 * dialog cannot disagree.
 *
 * ## Why this is a context rather than a prop
 *
 * The rejection arrives on a WebSocket frame, at the sync client, which has no
 * idea what is on screen. It could arrive while the brewer is in Settings, or
 * in another recipe, or with the tab in the background. So the client raises it
 * to a context and one dialog, mounted once in the shell, answers it.
 *
 * ## Buying, when there is anything to buy (PLAN-LAUNCH B3)
 *
 * The dialog was deliberately inert until M7. It is now the checkout, and the
 * shape of that is worth stating because four things have to be true at once
 * before a button appears: the deployment has a Paddle configuration
 * (`config.billing.enabled`, from `/api/config`), the visitor is **signed in**
 * (the checkout puts their user id in Paddle's `custom_data`, and that id is the
 * only link between a payment and an entitlement), the column is not the tier
 * they are already on, and **they do not already have a subscription**.
 *
 * That last one is `entitlement.subscribed`, and it is not the same question as
 * the tier (PLAN-UX-PASS A5). This checkout opens a *new* subscription every
 * time, so anybody Paddle is already billing must go to the portal instead —
 * Settings' "Manage billing", which reads the same field. Two of them could
 * reach a button here without it: a **paused** subscriber, whose account is
 * revoked to the free tier while the subscription is still theirs, so every
 * paid column was for sale to somebody who already pays; and a subscriber
 * moving *between* tiers, whose other column was never the one the tier check
 * excluded. Both would have ended up paying twice.
 *
 * When any of those is false the dialog is exactly what it was: three columns,
 * a comparison, and — on a deployment with no billing — the sentence saying so.
 * That is not a degraded mode, it is the mode every development build runs in.
 *
 * ## What happens after "Pay"
 *
 * Paddle tells the *browser* the payment succeeded, and separately tells the
 * *Worker* over a retrying webhook. Only the Worker may write an entitlement, so
 * the client cannot upgrade itself optimistically — it polls `/api/me` until the
 * plan actually changes (`waitForPlan`), then calls `session.refresh()`.
 *
 * That single refresh is what lifts the cap in place, and by a pleasant
 * accident of existing wiring rather than by anything added here: the new
 * `recipeLimit` is in `SyncProvider`'s effect dependencies, so the sync client
 * is rebuilt and the new socket carries the new plan to the Durable Object,
 * which is where the cap is enforced. No explicit reconnect nudge is needed, and
 * the assistant allowance never needed one — `ai/routes.ts` re-reads the plan
 * from D1 on every request.
 */

import { CheckIcon, Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { createContext, use, useCallback, useMemo, useRef, useState } from 'react';
import type { BillingConfig } from '@/auth/client';
import { waitForPlan } from '@/auth/client';
import { useSession } from '@/auth/session-context';
import { PlanName } from '@/components/plan-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { openCheckout } from '@/lib/paddle';
import type { Entitlement, Plan, PlanTier, UpgradeReason } from '@/lib/plan';
import {
  BLEND_LIMIT,
  JOURNAL_LIMIT,
  PLAN_TABLE,
  planName,
  priceLine,
  recommendedPlan,
} from '@/lib/plan';
import { cn } from '@/lib/utils';

/** Why the dialog is open. */
export interface PlanLimitPrompt {
  /** What the user ran into. Decides the headline and the recommended column. */
  readonly reason: UpgradeReason;
  /** The recipe, blend or journal cap that was hit. */
  readonly limit?: number;
  /** How many creates were refused — 1 for the button, more for an import. */
  readonly refused?: number;
}

interface PlanLimitContextValue {
  readonly prompt: PlanLimitPrompt | null;
  /** Raise the dialog. Called by the sync client's `onPlanLimit`, and by the drawer. */
  readonly show: (prompt: PlanLimitPrompt) => void;
  readonly dismiss: () => void;
}

const PlanLimitContext = createContext<PlanLimitContextValue | null>(null);

export function PlanLimitProvider({ children }: { readonly children: ReactNode }) {
  const [prompt, setPrompt] = useState<PlanLimitPrompt | null>(null);
  const show = useCallback((next: PlanLimitPrompt) => setPrompt(next), []);
  const dismiss = useCallback(() => setPrompt(null), []);
  const value = useMemo<PlanLimitContextValue>(
    () => ({ prompt, show, dismiss }),
    [prompt, show, dismiss],
  );
  return <PlanLimitContext value={value}>{children}</PlanLimitContext>;
}

/**
 * The upgrade prompt.
 *
 * Returns a no-op outside a provider, so a component rendered in isolation in a
 * test does not need one — the same rule `useSession` follows.
 */
export function usePlanLimit(): PlanLimitContextValue {
  return use(PlanLimitContext) ?? NOOP;
}

const NOOP: PlanLimitContextValue = {
  prompt: null,
  show: () => undefined,
  dismiss: () => undefined,
};

/**
 * The dialog itself. Mounted once, in the app shell.
 *
 * @param entitlement - The current plan, so the copy names the real cap rather
 *   than a constant that may have been overridden for this account, and so the
 *   column the user is already on is marked as such rather than sold to them.
 */
export function PlanLimitDialog({ entitlement }: { readonly entitlement: Entitlement }) {
  const { prompt, dismiss } = usePlanLimit();
  const session = useSession();
  const checkout = useCheckout(dismiss);
  const reason = prompt?.reason ?? 'recipe-cap';
  const recommended = recommendedPlan(reason);
  const limit = prompt?.limit ?? defaultLimitFor(reason, entitlement);
  const refused = prompt?.refused ?? 1;

  const billing = session.config.billing;
  // Three conditions, all of them necessary — see the file header. `user` is
  // read here rather than inside the column so a signed-out visitor gets the
  // comparison table with no dead buttons on it.
  const buyer = billing.enabled && session.user !== null ? session.user : null;

  return (
    <Dialog
      open={prompt !== null && !checkout.overlayOpen}
      onOpenChange={(open) => {
        if (open) return;
        checkout.cancel();
        dismiss();
      }}
    >
      <DialogContent className="sm:max-w-3xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle data-testid="upgrade-title">{headline(reason, limit)}</DialogTitle>
          <DialogDescription data-testid="upgrade-subtitle">
            {subtitle(reason, refused)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3">
          {PLAN_TABLE.map((tier) => (
            <TierColumn
              key={tier.id}
              tier={tier}
              recommended={tier.id === recommended}
              current={tier.id === entitlement.plan}
              prices={priceIdsFor(billing, tier.id)}
              busy={checkout.busy}
              buying={checkout.plan === tier.id}
              onBuy={
                // `subscribed` beside the tier check, not folded into `buyer`:
                // they are the same question asked of the column — "is this a
                // sale we can make?" — while `buyer` is who would pay. @see the
                // header for the two people the tier check alone let through.
                buyer === null || entitlement.subscribed || tier.id === entitlement.plan
                  ? null
                  : (priceId) =>
                      checkout.start({
                        plan: tier.id,
                        priceId,
                        clientToken: billing.clientToken ?? '',
                        userId: buyer.id,
                        email: buyer.email,
                      })
              }
            />
          ))}
        </div>

        {checkout.notice !== null && (
          <p className="text-xs text-muted-foreground" data-testid="checkout-notice">
            {checkout.notice}
          </p>
        )}

        {!billing.enabled && (
          <p className="text-xs text-muted-foreground" data-testid="billing-not-live">
            Billing isn’t live yet, but you’ll be able to buy these at launch. Nothing you have made
            is going anywhere in the meantime.
          </p>
        )}

        {/* Why every column went quiet. The `subscribed` gate above withholds
            all four buy buttons from somebody Paddle already bills, and until
            this paragraph the table simply stopped offering anything and gave
            no reason (PLAN-UX-PASS A5). It points at the portal rather than
            promising a tier change, because whether Paddle's portal allows one
            is still an open question for the owner. */}
        {entitlement.subscribed && (
          <p className="text-xs text-muted-foreground" data-testid="already-subscribed">
            You already have a subscription with us, so there is nothing to buy here.{' '}
            <strong>Manage billing</strong> on the Settings page opens it.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              checkout.cancel();
              dismiss();
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The price id for a tier, or `null` for the free one and for no billing. */
function priceIdsFor(billing: BillingConfig, plan: Plan): TierPriceIds | null {
  if (!billing.enabled || billing.prices === null || plan === 'trial') return null;
  return billing.prices[plan];
}

/** What a paid column needs in order to sell itself. */
interface TierPriceIds {
  readonly yearly: string;
}

/** One column of the comparison, and — on a paid tier a user may buy — the buttons. */
function TierColumn({
  tier,
  recommended,
  current,
  prices,
  busy,
  buying,
  onBuy,
}: {
  readonly tier: PlanTier;
  readonly recommended: boolean;
  readonly current: boolean;
  /** `null` when this deployment sells nothing, or this is the free tier. */
  readonly prices: TierPriceIds | null;
  /** True while *any* checkout is in flight; every button is disabled meanwhile. */
  readonly busy: boolean;
  /** True while this column's checkout is the one in flight. */
  readonly buying: boolean;
  /** `null` when this column is not purchasable — signed out, or already on it. */
  readonly onBuy: ((priceId: string) => void) | null;
}) {
  const purchasable = prices !== null && onBuy !== null;

  return (
    <section
      data-testid={`tier-${tier.id}`}
      data-recommended={recommended}
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3',
        recommended ? 'border-primary bg-primary/5' : 'border-border bg-muted/30',
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          <PlanName plan={tier.id} iconClassName="size-4" />
        </h3>
        {recommended ? (
          <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
            Recommended
          </span>
        ) : current ? (
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
            Your plan
          </span>
        ) : null}
      </header>

      <p className="text-sm font-medium" data-testid={`price-${tier.id}`}>
        {priceLine(tier)}
      </p>
      <p className="text-xs text-muted-foreground">{tier.summary}</p>

      <ul className="flex flex-col gap-1 pt-1">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start gap-1.5 text-xs">
            <CheckIcon
              className={cn(
                'mt-0.5 size-3 shrink-0',
                recommended ? 'text-primary' : 'text-muted-foreground',
              )}
              aria-hidden="true"
            />
            {feature}
          </li>
        ))}
      </ul>

      {/* Both tiers are sold yearly only, so a column has one button. */}
      {purchasable && (
        <div className="flex flex-col gap-1 pt-2">
          <Button
            size="sm"
            disabled={busy}
            data-testid={`buy-${tier.id}-yearly`}
            onClick={() => onBuy(prices.yearly)}
          >
            {buying && <Loader2Icon className="size-3 animate-spin" aria-hidden="true" />}
            {tier.yearlyPrice ?? ''} a year
          </Button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The purchase itself
// ---------------------------------------------------------------------------

/** Everything one press of a purchase button needs. */
interface CheckoutStart {
  readonly plan: Plan;
  readonly priceId: string;
  readonly clientToken: string;
  readonly userId: string;
  readonly email: string;
}

/** The dialog's view of an in-flight purchase. */
interface CheckoutState {
  /** The tier being bought, or `null`. Drives the spinner on one column. */
  readonly plan: Plan | null;
  /** True while anything is in flight; every button is disabled meanwhile. */
  readonly busy: boolean;
  /**
   * True while Paddle's overlay is on screen. The dialog hides itself for the
   * duration: a modal left open underneath swallows the overlay's own close
   * button as an outside-click, so closing the overlay took two presses.
   */
  readonly overlayOpen: boolean;
  /** A line under the columns, or `null`. Progress and failure, never a toast. */
  readonly notice: string | null;
  readonly start: (request: CheckoutStart) => void;
  /** Stop waiting. Called when the dialog is closed by hand. */
  readonly cancel: () => void;
}

/**
 * Drive one purchase from button to lifted cap.
 *
 * ## Why the messages are inline rather than toasts
 *
 * A toast fires *behind* an open modal, where the person who pressed the button
 * is not looking. Everything this flow has to say is about the dialog they are
 * still in, so it is said in the dialog.
 *
 * ## Why closing the overlay is silent, and final
 *
 * `checkout.closed` is somebody deciding not to buy. It gets no message, no
 * error state and no "are you sure" — it is the most common outcome of opening
 * a pricing dialog and treating it as a failure would be obnoxious. And because
 * the pricing dialog hid itself while the overlay was up, `closed` dismisses
 * the whole prompt: the X was them leaving, and bringing the comparison table
 * back would just be one more thing to close. A `completed` purchase is the
 * opposite case — the dialog *returns*, because the activation copy below is
 * about it and has nowhere else to be said.
 *
 * ## Why a timeout is not a failed payment
 *
 * `waitForPlan` gives up after a minute. The payment still went through and the
 * webhook is still coming; only our patience ran out. The copy says exactly
 * that, because "something went wrong" after a successful card charge is the
 * message that generates a support ticket and a chargeback.
 */
function useCheckout(dismiss: () => void): CheckoutState {
  const session = useSession();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  /** Cancels an in-flight `waitForPlan`, so closing the dialog stops the poll. */
  const waiting = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    waiting.current?.abort();
    waiting.current = null;
    setPlan(null);
    setNotice(null);
    setOverlayOpen(false);
  }, []);

  const { refresh } = session;
  const start = useCallback(
    (request: CheckoutStart) => {
      setPlan(request.plan);
      setNotice(null);
      void (async () => {
        let outcome: Awaited<ReturnType<typeof openCheckout>>;
        try {
          outcome = await openCheckout({
            token: request.clientToken,
            priceId: request.priceId,
            email: request.email,
            userId: request.userId,
            onOpened: () => setOverlayOpen(true),
          });
        } catch {
          setOverlayOpen(false);
          setPlan(null);
          setNotice('We could not open the checkout. Check your connection and try again.');
          return;
        }
        setOverlayOpen(false);
        if (outcome !== 'completed') {
          setPlan(null);
          dismiss();
          return;
        }

        setNotice('Your payment went through. We’re activating your plan…');
        const controller = new AbortController();
        waiting.current = controller;
        const granted = await waitForPlan(request.plan, controller.signal);
        waiting.current = null;
        // Closed by hand while we were waiting: `cancel` has already cleared
        // the state, and writing to it again would resurrect the dialog's copy.
        if (controller.signal.aborted) return;

        // One refresh, and it does everything: `/api/me` carries the session and
        // the entitlement together, and the new `recipeLimit` rebuilds the sync
        // client, whose fresh socket carries the plan to the Durable Object that
        // enforces the cap.
        await refresh();
        setPlan(null);
        if (granted) {
          setNotice(null);
          dismiss();
          return;
        }
        setNotice(
          'Your payment went through. We are still activating your plan. It will be there in a moment, or after a reload.',
        );
      })();
    },
    [dismiss, refresh],
  );

  return useMemo<CheckoutState>(
    () => ({ plan, busy: plan !== null, overlayOpen, notice, start, cancel }),
    [plan, overlayOpen, notice, start, cancel],
  );
}

/**
 * The cap to quote when the prompt did not name one.
 *
 * Only the recipe meter's number is per-account — support can raise it — so it
 * is the one read off the entitlement; the blend and journal caps are the same
 * five for every free account and come from the tier table.
 */
function defaultLimitFor(reason: UpgradeReason, entitlement: Entitlement): number {
  switch (reason) {
    case 'blend-cap':
      return BLEND_LIMIT;
    case 'journal-cap':
      return JOURNAL_LIMIT;
    default:
      return entitlement.recipeLimit;
  }
}

/** The headline states the fact that was hit, not the thing being sold. */
function headline(reason: UpgradeReason, limit: number): string {
  switch (reason) {
    case 'recipe-cap':
      return `Your plan includes ${limit} recipes`;
    case 'blend-cap':
      return `Your plan includes ${limit} blends`;
    case 'journal-cap':
      return `Your plan includes ${limit} journals`;
    case 'ai-allowance':
      return 'You are out of assistant messages this month';
    case 'ai-locked':
      return 'That needs the assistant';
    case 'telemetry':
      return 'Fermentation charts are a Beerwright feature';
    case 'insights':
      return 'Your trends live in Beerwright';
    case 'feedback':
      return `Tasting sheets come with ${planName('brewmaster')} and ${planName('beerwright')}`;
    // The invite dialog's own sentence, split at its full stop: the first
    // half is the fact that was hit, the second is what it costs the people
    // being asked, which is nothing (D-CO4).
    case 'coBrewers':
      return `Inviting co-brewers comes with ${planName('brewmaster')} and ${planName('beerwright')}`;
    case 'browse':
      return 'Plans';
  }
}

function subtitle(reason: UpgradeReason, refused: number): string {
  switch (reason) {
    // Archiving is named here on purpose (PLAN-ARCHIVE §F). It is the obvious
    // thing to reach for at the cap, it does not help, and a brewer who
    // archives three and meets this dialog again has been misled by us rather
    // than by their own optimism.
    case 'recipe-cap':
      return `${
        refused === 1 ? 'We could not save that recipe' : `We could not save ${refused} recipes`
      }: your workspace is already at the limit. Archived items still count toward the limit, so delete one you no longer need, or move to a plan with no cap.`;
    case 'blend-cap':
      return `${
        refused === 1 ? 'We could not save that blend' : `We could not save ${refused} blends`
      }: your workspace is already at the limit. Delete one you no longer need, or move to a plan with no cap.`;
    case 'journal-cap':
      return `${
        refused === 1 ? 'We could not save that journal' : `We could not save ${refused} journals`
      }: your workspace is already at the limit. Delete one you no longer need, or move to a plan with no cap.`;
    case 'ai-allowance':
      return `Your allowance comes back at the start of next month. ${planName('beerwright')} carries a much larger one.`;
    case 'ai-locked':
      return `The assistant reads your real workspace and proposes changes you review. ${planName('beerwright')} is the tier built around it.`;
    // Nothing was refused here either — a device is still recording and the
    // page is still computing. What is missing is the picture, so the copy says
    // which tier draws it rather than implying something was lost.
    case 'telemetry':
      return `A Tilt, RAPT Pill or iSpindel keeps posting readings whatever plan you are on, and they are all kept. ${planName('beerwright')} is where you see them charted.`;
    case 'insights':
      return `We compute efficiency, attenuation, yield, cadence and the spread of everything you have brewed from your own batches. ${planName('beerwright')} opens the whole page.`;
    // Nothing was refused here either: a card already in somebody's hand goes on
    // collecting, so the copy says which tier reads the sheets rather than
    // implying they were lost.
    case 'feedback':
      return `A card you have printed keeps collecting sheets whatever plan you are on, and we keep every one. Either paid plan lets you read them, beside the competition sheets you scan.`;
    case 'coBrewers':
      return "The people you invite don't need a paid plan.";
    // Nothing was refused and nothing ran out — they pressed a button. So the
    // subtitle states what the table is rather than what went wrong.
    case 'browse':
      return 'Here is what each tier includes. Everything you have made stays yours on any of them.';
  }
}
